import { Button, Tooltip } from '@/components'
import { RegularModal } from '@/components_deprecated'
import { useUser } from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { differenceInMinutes, parseISO } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import {
  CREATE_CALENDAR_ITEM_COMMENT,
  MARK_READ_CALENDAR_ITEM_COMMENTS,
} from './mutations'
import { CALENDAR_ITEM_COMMENTS } from './queries'
import type { CalendarItemCommentNode } from './types.graphql'

export type CalendarItemWithComments = {
  id: string
  title: string
  numComments: number
  numCommentsUnread: number
}

type DisplayComment = CalendarItemCommentNode & {
  type: 'single' | 'first' | 'middle' | 'last'
  showTimestamp: boolean
}

const ButtonWrapper = styled.span`
  position: relative;
  display: inline-flex;
`

const Badge = styled.span`
  position: absolute;
  top: -0.35rem;
  right: -0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.1rem;
  height: 1.1rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: ${props => props.theme.colors.secondary};
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
`

const PlannerModal = styled(RegularModal)`
  width: min(56rem, 92vw);
  max-height: calc(100vh - 2rem);
  border: 0;
  overflow: hidden;

  div.content {
    padding: 0;
  }

  > div:not(.content) {
    display: none;
  }
`

const ChatWrapper = styled.div<{ $inline?: boolean }>`
  display: grid;
  grid-template-rows: ${props =>
    props.$inline ? 'minmax(14rem, 45vh) auto' : 'minmax(22rem, 65vh) auto'};
  width: ${props => (props.$inline ? '100%' : 'min(56rem, 92vw)')};
  background: white;
`

const Comments = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
  padding: ${props => (props.$inline ? '1rem' : '2rem 2.4rem')};
  background: white;
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.theme.colors.gray4};
  font-size: 0.95rem;
`

const Timestamp = styled.div<{ $isUser: boolean }>`
  align-self: center;
  margin: 0.55rem 0 0.4rem;
  color: #7f8fa6;
  font-size: 1.05rem;
`

const CommentRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  align-items: flex-end;
  justify-content: ${props => (props.$isUser ? 'flex-end' : 'flex-start')};
  gap: 0.5rem;
`

const Avatar = styled.div<{ $src?: string | null; $visible: boolean }>`
  flex: 0 0 2rem;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: ${props =>
    props.$src
      ? `url(${props.$src}) center / cover`
      : props.theme.colors.gray8};
  color: ${props => props.theme.colors.gray3};
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};
`

const Bubble = styled.div<{
  $isUser: boolean
  $type: DisplayComment['type']
}>`
  max-width: min(25rem, 72%);
  padding: 0.75rem 1.1rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.35;
  color: ${props => (props.$isUser ? 'white' : props.theme.colors.gray1)};
  background: ${props =>
    props.$isUser ? '#4563e6' : props.theme.colors.white};
  border: 1px solid
    ${props => (props.$isUser ? '#4563e6' : props.theme.colors.gray8)};
  border-radius: ${props => {
    if (props.$type === 'single') return '18px'
    if (props.$isUser) {
      return props.$type === 'first'
        ? '18px 18px 4px 18px'
        : props.$type === 'last'
        ? '18px 4px 18px 18px'
        : '18px 4px 4px 18px'
    }
    return props.$type === 'first'
      ? '18px 18px 18px 4px'
      : props.$type === 'last'
      ? '4px 18px 18px 18px'
      : '4px 18px 18px 4px'
  }};
`

const InputRow = styled.div<{ $inline?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${props => (props.$inline ? '0.75rem' : '1.25rem')};
  align-items: center;
  min-height: ${props => (props.$inline ? '4.75rem' : '7rem')};
  padding: ${props => (props.$inline ? '0.75rem 1rem' : '1.2rem 2.4rem')};
  border-top: 1px solid ${props => props.theme.colors.gray8};
`

const Input = styled.textarea<{ $inline?: boolean }>`
  min-height: 3.5rem;
  max-height: 6rem;
  resize: vertical;
  padding: 0.6rem 0;
  border: 0;
  background: transparent;
  outline: none;
  color: ${props => props.theme.colors.gray1};
  font-size: ${props => (props.$inline ? '1rem' : '1.45rem')};
  line-height: 1.35;

  &::placeholder {
    color: ${props => props.theme.colors.gray4};
  }
`

const SendButton = styled.button<{ $inline?: boolean }>`
  min-width: ${props => (props.$inline ? '4.25rem' : '7rem')};
  height: ${props => (props.$inline ? '2.75rem' : '3.9rem')};
  padding: ${props => (props.$inline ? '0 1rem' : '0 1.7rem')};
  border: 0;
  border-radius: ${props => (props.$inline ? '10px' : '14px')};
  background: #4563e6;
  color: white;
  font-size: ${props => (props.$inline ? '1rem' : '1.45rem')};
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`

const getInitials = (name?: string | null) =>
  (name ?? '')
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

function isCurrentUserComment(
  comment: CalendarItemCommentNode,
  currentUserId: string
) {
  return comment.createdBy?.id === currentUserId
}

export function CalendarItemCommentsPanel({
  calendarItem,
  me,
  inline,
}: {
  calendarItem: CalendarItemWithComments
  me: { id: string }
  inline?: boolean
}) {
  const translations = useTranslate({
    send: 'calendar.item.comments.send',
    noComments: 'calendar.item.comments.no-comments',
    unknownUser: 'common.unknown-user',
  })

  const commentsRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const markedReadRef = useRef(false)
  const [newComment, setNewComment] = useState('')

  const variables = useMemo(
    () => ({ calendarItemId: calendarItem.id }),
    [calendarItem.id]
  )
  const { data } = useQuery(CALENDAR_ITEM_COMMENTS, {
    variables,
    pollInterval: 5000,
    fetchPolicy: 'cache-and-network',
  })

  const [createComment, { loading: createLoading }] = useMutation(
    CREATE_CALENDAR_ITEM_COMMENT,
    {
      refetchQueries: [{ query: CALENDAR_ITEM_COMMENTS, variables }],
    }
  )
  const [markRead] = useMutation(MARK_READ_CALENDAR_ITEM_COMMENTS)

  useEffect(() => {
    if (markedReadRef.current || !calendarItem.numCommentsUnread) return
    markedReadRef.current = true
    void markRead({ variables })
  }, [calendarItem.numCommentsUnread, markRead, variables])

  const comments = useMemo<DisplayComment[]>(() => {
    const source = data?.calendarItemComments ?? []
    return source.map((comment, index) => {
      const prevComment = source[index - 1]
      const nextComment = source[index + 1]
      const sameUserAsPrev =
        prevComment?.createdBy?.id === comment.createdBy?.id
      const sameUserAsNext =
        nextComment?.createdBy?.id === comment.createdBy?.id
      const showTimestamp =
        !prevComment ||
        differenceInMinutes(
          parseISO(comment.createdAt),
          parseISO(prevComment.createdAt)
        ) > 15
      const type =
        !sameUserAsPrev && !sameUserAsNext
          ? 'single'
          : !sameUserAsPrev
          ? 'first'
          : !sameUserAsNext
          ? 'last'
          : 'middle'

      return { ...comment, type, showTimestamp }
    })
  }, [data?.calendarItemComments])

  useEffect(() => {
    commentsRef.current?.scrollTo({
      top: commentsRef.current.scrollHeight,
    })
  }, [comments.length])

  useEffect(() => {
    const focusInput = () => {
      const input = inputRef.current
      if (!input) return

      input.focus()

      const selectionStart = input.value.length
      input.setSelectionRange(selectionStart, selectionStart)
    }

    focusInput()

    const animationFrameId = window.requestAnimationFrame(focusInput)
    const timeoutId = window.setTimeout(focusInput, 250)

    return () => {
      window.cancelAnimationFrame(animationFrameId)
      window.clearTimeout(timeoutId)
    }
  }, [])

  async function handleSubmit() {
    const text = newComment.trim()
    if (!text) return
    await createComment({
      variables: {
        input: {
          calendarItem: calendarItem.id,
          text,
        },
      },
    })
    setNewComment('')
  }

  return (
    <ChatWrapper $inline={inline}>
      <Comments ref={commentsRef} $inline={inline}>
        {comments.length === 0 ? (
          <EmptyState>{translations.noComments}</EmptyState>
        ) : (
          comments.map(comment => {
            const isUser = isCurrentUserComment(comment, me.id)
            const showAvatar =
              !isUser && (comment.type === 'single' || comment.type === 'last')

            return (
              <div key={comment.id}>
                {comment.showTimestamp && (
                  <Timestamp $isUser={isUser}>
                    {formatCommentTime(comment.createdAt)}
                  </Timestamp>
                )}
                <CommentRow $isUser={isUser}>
                  {!isUser && (
                    <Avatar
                      $src={comment.createdBy?.profilePicture}
                      $visible={showAvatar}
                      title={
                        comment.createdBy?.fullName ?? translations.unknownUser
                      }
                    >
                      {!comment.createdBy?.profilePicture &&
                        getInitials(comment.createdBy?.fullName)}
                    </Avatar>
                  )}
                  <Bubble $isUser={isUser} $type={comment.type}>
                    {comment.text}
                  </Bubble>
                </CommentRow>
              </div>
            )
          })
        )}
      </Comments>
      <InputRow $inline={inline}>
        <Input
          ref={inputRef}
          $inline={inline}
          value={newComment}
          placeholder="Aa"
          autoFocus
          onChange={event => setNewComment(event.target.value)}
          onKeyDown={event => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault()
              void handleSubmit()
            }
          }}
        />
        <SendButton
          $inline={inline}
          disabled={createLoading || !newComment.trim()}
          onClick={() => void handleSubmit()}
        >
          {translations.send}
        </SendButton>
      </InputRow>
    </ChatWrapper>
  )
}

function CalendarItemCommentsModal({
  calendarItem,
  me,
  onClose,
}: {
  calendarItem: CalendarItemWithComments
  me: { id: string }
  onClose: () => void
}) {
  return (
    <PlannerModal
      width="min-content"
      onCancel={onClose}
      cancelOnClickOutside
      cancelOnEscape
    >
      <CalendarItemCommentsPanel calendarItem={calendarItem} me={me} />
    </PlannerModal>
  )
}

function formatCommentTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseISO(value))
}

export function CalendarItemCommentsButton({
  calendarItem,
}: {
  calendarItem: CalendarItemWithComments
}) {
  const translations = useTranslate({
    comments: 'common.comments',
  })
  const me = useUser()
  const theme = useTheme()
  const addPrompt = usePrompt()

  return (
    <Tooltip content={translations.comments}>
      <ButtonWrapper>
        <Button
          size="icon-lg"
          variant="outline"
          onClick={event => {
            event.stopPropagation()
            void addPrompt<null>(resolve => (
              <CalendarItemCommentsModal
                calendarItem={calendarItem}
                me={me}
                onClose={() => resolve(null)}
              />
            ))
          }}
        >
          <Icon icon="comment" color={theme.colors.primary} size="24" />
        </Button>
        {calendarItem.numComments > 0 && (
          <Badge>{calendarItem.numComments}</Badge>
        )}
      </ButtonWrapper>
    </Tooltip>
  )
}
